'use client'

import { usePathname } from 'next/navigation'
import { isSitesControlPlanePath } from '@/lib/sites/control-plane'
import { isRadarThemedPath } from '@/lib/radar/theme-routes'

/**
 * The private Radar Sites control plane — exact `/sites` and everything under
 * it, never `/sitesomething` — is excluded from BOTH shells. Beyond the visual
 * chrome, being outside the marketing shell is what keeps Ga4SpaTracking,
 * AIReferrerTracker, CtaTracker, RecentArticles, the chat widget and Vercel
 * Analytics from mounting on a signed-in workspace surface, and the consent
 * shell exclusion keeps the cookie banner off it — the control plane runs no
 * marketing analytics at all, so there is nothing there to consent to.
 *
 * The predicate itself now lives in `@/lib/sites/control-plane`: AnalyticsGate
 * and the Sentry client initializer gate on the same boundary, and a telemetry
 * boundary that disagrees with the shell boundary by one path is exactly the
 * kind of drift that leaks an invitation token.
 */

type ConditionalShellProps = {
  children: React.ReactNode
}

function isPlatformAppPath(pathname: string | null): boolean {
  return pathname?.startsWith('/platform/app') ?? false
}

/**
 * Paid-ad landing pages. These render their own minimal logo-only header and
 * must not show site navigation, because every extra link is an exit from a
 * page someone paid to send traffic to. Consent chrome still applies — see
 * ConditionalConsentShell below, which deliberately does NOT exclude these.
 */
function isPaidLandingPath(pathname: string | null): boolean {
  return pathname === '/platform/start'
}

/**
 * Hides visual marketing chrome on every route the Radar app frame owns (the
 * /platform/app dashboard and the owner pilot at /admin/monitoring and
 * /admin/improvements, one list in theme-routes.ts), the Radar Sites control
 * plane, /ar/*, and paid-ad landing pages. Arabic landing pages provide their
 * own RTL navigation instead of mirroring the English header/footer.
 *
 * The Radar frame is a full-height app shell with a sticky rail; the site
 * header above it pushed the rail's foot below the fold (PIX-207, 5 Nov 2026).
 */
export default function ConditionalMarketingShell({
  children,
}: ConditionalShellProps) {
  const pathname = usePathname()
  if (isPlatformAppPath(pathname) || isRadarThemedPath(pathname)) {
    return null
  }
  if (isSitesControlPlanePath(pathname)) return null
  if (isPaidLandingPath(pathname)) return null
  if (pathname === '/ar' || pathname?.startsWith('/ar/')) return null
  return <>{children}</>
}

/**
 * Consent controls are privacy infrastructure, not marketing chrome. Keep them
 * available on Arabic landing pages while preserving the authenticated app's
 * existing shell exclusion.
 */
export function ConditionalConsentShell({ children }: ConditionalShellProps) {
  const pathname = usePathname()
  if (isPlatformAppPath(pathname)) return null
  if (isSitesControlPlanePath(pathname)) return null
  return <>{children}</>
}
